import { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { AppContext } from '@/App';
import axios from 'axios';
import {
  Plus, Search, Truck, CheckCircle, Package, Pencil, Trash2, X, Save, Loader2,
  Download, Mail, Send, Briefcase, Building2, ChevronDown, AlertCircle,
} from 'lucide-react';
import { canUserEdit, canUserDelete } from '@/utils/permissions';
import { miscRowError, hasMiscErrors } from '@/utils/miscValidation';
import HsnCommodityPicker from '@/components/HsnCommodityPicker';
import AdvancePaymentSplitConfig from '../../components/AdvancePaymentSplitConfig';
import { ProjectSelect } from '@/components/ProjectSelect';
import { useProjectDirectory } from '@/hooks/useProjectDirectory';
import { matchesProjectFilter, projectFilterOptions } from '@/utils/projectIdentity';

const PAGE_PATH = '/projects/purchase-orders';

// ----------- Vendor master-data dropdown (mirrors Stores PO V2) -----------
function VendorDropdown({ displayValue, onSelect, placeholder = 'Search vendor…', disabled = false }) {
  const { API } = useContext(AppContext);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const timeoutRef = useRef(null);

  const doSearch = useCallback((term) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/master-data/stores/vendors`);
        const vendors = r.data?.data || [];
        const t = term.toLowerCase();
        const filtered = vendors.filter((v) =>
          (v.vendorName || '').toLowerCase().includes(t) ||
          (v.vendorCode || '').toLowerCase().includes(t),
        ).slice(0, 25);
        setOptions(filtered);
      } catch {
        setOptions([]);
      }
      setLoading(false);
    }, 250);
  }, [API]);

  useEffect(() => { if (isOpen) doSearch(searchTerm); }, [isOpen, searchTerm, doSearch]);
  useEffect(() => {
    const close = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button type="button" disabled={disabled} onClick={() => setIsOpen(!isOpen)}
        className={`w-full px-3 py-2 border rounded-lg text-sm text-left flex items-center justify-between ${
          disabled ? 'bg-gray-100 border-gray-200' : 'border-gray-300 hover:border-amber-400'
        }`}
        data-testid="project-po-vendor-dropdown">
        <span className={displayValue ? 'text-gray-900 truncate' : 'text-gray-400'}>{displayValue || placeholder}</span>
        <ChevronDown size={16} className={`text-gray-400 transition flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-72 overflow-hidden">
          <div className="p-2 border-b border-gray-100 sticky top-0 bg-white">
            <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg">
              <Search size={16} className="text-gray-400" />
              <input autoFocus value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Type to search…" className="flex-1 bg-transparent outline-none text-sm" />
              {loading && <Loader2 size={16} className="animate-spin text-gray-400" />}
            </div>
          </div>
          <div className="max-h-56 overflow-y-auto">
            {options.length === 0 ? (
              <div className="px-3 py-4 text-center text-gray-500 text-sm">
                {loading ? 'Loading…' : 'No vendors found'}
              </div>
            ) : options.map((v) => (
              <button key={v.id} type="button"
                onClick={() => { onSelect(v); setIsOpen(false); setSearchTerm(''); }}
                className="w-full px-3 py-2 text-left text-sm hover:bg-amber-50 transition flex items-center gap-2">
                <Building2 size={14} className="text-gray-400" />
                <div className="flex-1">
                  <div className="font-medium text-gray-900">{v.vendorName}</div>
                  <div className="text-xs text-gray-500">
                    {v.vendorCode} | {v.city || 'N/A'}
                    {v.gstin && <span className="ml-2 text-amber-600">GST: {v.gstin}</span>}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ----------- Commodity + HSN search (Material PO) -----------
function CommodityDropdown({ displayValue, onSelect, placeholder = 'Search commodity…' }) {
  const { API } = useContext(AppContext);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const timeoutRef = useRef(null);

  const doSearch = useCallback((term) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (term.length < 2) { setOptions([]); setLoading(false); return; }
    timeoutRef.current = setTimeout(async () => {
      setLoading(true);
      const [comm, sac] = await Promise.allSettled([
        axios.get(`${API}/master-data/stores/commodities/search`, { params: { q: term, limit: 20 } }),
        axios.get(`${API}/master-data/stores/service_sac/search`, { params: { q: term, limit: 10 } }),
      ]);
      let results = [];
      if (comm.status === 'fulfilled') results = comm.value.data?.data || [];
      if (sac.status === 'fulfilled') {
        const sacRaw = sac.value.data?.data || [];
        const sacFormatted = sacRaw.map((s) => s.commodityCode ? s : ({
          id: `sac_${s.sacCode || ''}`,
          commodityCode: s.sacCode || '',
          commodityName: s.sacDescription || '',
          hsnCode: s.sacCode || '',
          gstRate: s.gstRate || 18,
          itemType: 'service',
        }));
        const seen = new Set(results.map((r) => r.commodityCode));
        results = [...results, ...sacFormatted.filter((s) => !seen.has(s.commodityCode))];
      }
      setOptions(results);
      setLoading(false);
    }, 280);
  }, [API]);

  useEffect(() => { if (isOpen && searchTerm.length >= 2) doSearch(searchTerm); }, [isOpen, searchTerm, doSearch]);
  useEffect(() => {
    const close = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setIsOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button type="button" onClick={() => setIsOpen(!isOpen)}
        className="w-full px-3 py-2 border rounded-lg text-sm text-left flex items-center justify-between border-gray-300 hover:border-amber-400">
        <span className={displayValue ? 'text-gray-900 truncate' : 'text-gray-400'}>{displayValue || placeholder}</span>
        <ChevronDown size={16} className={`text-gray-400 transition flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-72 overflow-hidden">
          <div className="p-2 border-b border-gray-100 sticky top-0 bg-white">
            <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg">
              <Search size={16} className="text-gray-400" />
              <input autoFocus value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Type at least 2 characters…" className="flex-1 bg-transparent outline-none text-sm" />
              {loading && <Loader2 size={16} className="animate-spin text-gray-400" />}
            </div>
          </div>
          <div className="max-h-56 overflow-y-auto">
            {searchTerm.length < 2 ? (
              <div className="px-3 py-4 text-center text-gray-500 text-sm">Type at least 2 characters to search</div>
            ) : options.length === 0 ? (
              <div className="px-3 py-4 text-center text-gray-500 text-sm">{loading ? 'Loading…' : 'No matches'}</div>
            ) : options.map((item) => (
              <button key={item.id || item.commodityCode} type="button"
                onClick={() => { onSelect(item); setIsOpen(false); setSearchTerm(''); }}
                className="w-full px-3 py-2 text-left text-sm hover:bg-amber-50 transition">
                <div className="font-medium text-gray-900 truncate">
                  {item.itemType === 'service' && <span className="text-xs bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded mr-1">SAC</span>}
                  {item.commodityName}
                </div>
                <div className="text-xs text-gray-500">
                  {item.itemType === 'service' ? 'SAC' : 'HSN'}: {item.hsnCode} | GST: {item.gstRate}%
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ----------- SAC dropdown (Service PO) -----------
function SACSearchDropdown({ displayValue, onSelect, placeholder = 'Search SAC code or service…' }) {
  const { API } = useContext(AppContext);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const timeoutRef = useRef(null);

  const doSearch = useCallback((term) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (term.length < 2) { setOptions([]); setLoading(false); return; }
    timeoutRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/master-data/stores/service_sac/search`, { params: { q: term, limit: 30 } });
        setOptions(r.data?.data || []);
      } catch { setOptions([]); }
      setLoading(false);
    }, 280);
  }, [API]);

  useEffect(() => { if (isOpen && searchTerm.length >= 2) doSearch(searchTerm); }, [isOpen, searchTerm, doSearch]);
  useEffect(() => {
    const close = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setIsOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button type="button" onClick={() => setIsOpen(!isOpen)}
        className="w-full px-3 py-2 border rounded-lg text-sm text-left flex items-center justify-between border-gray-300 hover:border-indigo-400">
        <span className={displayValue ? 'text-gray-900 truncate' : 'text-gray-400'}>{displayValue || placeholder}</span>
        <ChevronDown size={16} className={`text-gray-400 transition flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-72 overflow-hidden">
          <div className="p-2 border-b border-gray-100 sticky top-0 bg-white">
            <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg">
              <Search size={16} className="text-gray-400" />
              <input autoFocus value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Type SAC code or service name…" className="flex-1 bg-transparent outline-none text-sm" />
              {loading && <Loader2 size={16} className="animate-spin text-gray-400" />}
            </div>
          </div>
          <div className="max-h-56 overflow-y-auto">
            {searchTerm.length < 2 ? (
              <div className="px-3 py-4 text-center text-gray-500 text-sm">Type at least 2 characters to search</div>
            ) : options.length === 0 ? (
              <div className="px-3 py-4 text-center text-gray-500 text-sm">{loading ? 'Loading…' : 'No SAC codes found'}</div>
            ) : options.filter((it) => !it.codeLength || it.codeLength === 6).map((item) => (
              <button key={item.sacCode} type="button"
                onClick={() => { onSelect(item); setIsOpen(false); setSearchTerm(''); }}
                className="w-full px-3 py-2 text-left text-sm hover:bg-indigo-50 transition">
                <div className="font-medium text-gray-900 truncate">{item.sacDescription}</div>
                <div className="text-xs text-gray-500">SAC: {item.sacCode} | GST: {item.gstRate}%</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ----------- Page-level helpers -----------
const blankItem = (poType = 'material') => ({
  commodityId: '', commodityName: '', commodityCode: '', hsnCode: '',
  gstRate: 18, quantity: '', uom: poType === 'service' ? 'Job' : 'Nos',
  rate: '', amount: 0, itemType: poType,
});
const blankMisc = () => ({ description: '', amount: '' });

const CURRENCY_OPTIONS = [
  { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
  { code: 'CNY', symbol: '¥', name: 'Chinese Yuan' },
  { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar' },
];
const currencySymbol = (code) => (CURRENCY_OPTIONS.find((c) => c.code === code) || { symbol: code }).symbol;

const emptyForm = (poType = 'material') => ({
  projectId: '',
  projectName: '',
  poType,
  vendorName: '',
  vendorCode: '',
  vendorAddress: '',
  vendorGst: '',
  vendorContact: '',
  vendorContactPerson: '',
  vendorEmail: '',
  vendorCity: '',
  vendorState: '',
  vendorCountry: '',
  poDate: new Date().toISOString().slice(0, 10),
  deliveryDate: '',
  deliveryEndDate: '',
  deliveryAddress: '',
  paymentTerms: '',
  termsConditions: [],
  currency: 'INR',
  items: [blankItem(poType)],
  miscCharges: [],
  remarks: '',
  status: 'Ordered',
  advancePaymentConfig: { enabled: false, splits: [] },
});

function ProjectPurchaseOrders() {
  const { user, API } = useContext(AppContext);
  const canEdit = canUserEdit(user, PAGE_PATH);
  const canDelete = canUserDelete(user, PAGE_PATH);

  const [orders, setOrders] = useState([]);
  const [paymentTermsList, setPaymentTermsList] = useState([]);
  const [termsConditionsList, setTermsConditionsList] = useState([]);
  const [uomList, setUomList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterProject, setFilterProject] = useState('all');
  const [showOnlyUnemailed, setShowOnlyUnemailed] = useState(false);
  const [search, setSearch] = useState('');
  const [poTab, setPoTab] = useState('material'); // material | service

  const [showModal, setShowModal] = useState(false);
  const [retainedProject, setRetainedProject] = useState(null);
  const directory = useProjectDirectory({ includeCompleted: true, refreshKey: showModal });
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm('material'));
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  // Email-PO state
  const [emailPo, setEmailPo] = useState(null);
  const [emailForm, setEmailForm] = useState({ to: '', cc: '', subject: '', body: '' });
  const [emailing, setEmailing] = useState(false);
  const [emailResult, setEmailResult] = useState(null);
  const [emailConfig, setEmailConfig] = useState({ senderEmail: '', isSandbox: false });

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [poRes, ptRes, tcRes, uomRes] = await Promise.all([
        axios.get(`${API}/projects/purchase-orders`),
        axios.get(`${API}/master-data/stores/payment_terms`).catch(() => ({ data: { data: [] } })),
        axios.get(`${API}/master-data/stores/terms_conditions`).catch(() => ({ data: { data: [] } })),
        axios.get(`${API}/master-data/stores/uom`).catch(() => ({ data: { data: [] } })),
      ]);
      setOrders(poRes.data || []);
      setPaymentTermsList(ptRes.data?.data || []);
      setTermsConditionsList(tcRes.data?.data || []);
      setUomList(uomRes.data?.data || []);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setRetainedProject(null);
    setForm(emptyForm(poTab));
    setShowModal(true);
  };

  const openEdit = (po) => {
    const t = po.poType === 'service' ? 'service' : 'material';
    if (t !== poTab) setPoTab(t);
    setEditingId(po.id);
    setRetainedProject({ id: po.projectId || '', name: po.projectName || po.projectId || 'Historical project' });
    setForm({
      projectId: po.projectId || '',
      projectName: po.projectName || '',
      poType: t,
      vendorName: po.vendorName || po.supplier || '',
      vendorCode: po.vendorCode || '',
      vendorAddress: po.vendorAddress || '',
      vendorGst: po.vendorGst || '',
      vendorContact: po.vendorContact || '',
      vendorContactPerson: po.vendorContactPerson || '',
      vendorEmail: po.vendorEmail || '',
      vendorCity: po.vendorCity || '',
      vendorState: po.vendorState || '',
      vendorCountry: po.vendorCountry || '',
      poDate: po.poDate || po.orderDate || new Date().toISOString().slice(0, 10),
      deliveryDate: po.deliveryDate || '',
      deliveryEndDate: po.deliveryEndDate || '',
      deliveryAddress: po.deliveryAddress || '',
      paymentTerms: po.paymentTerms || '',
      termsConditions: Array.isArray(po.termsConditions)
        ? po.termsConditions
        : (po.termsConditions ? [po.termsConditions] : []),
      currency: po.currency || 'INR',
      currencyLocked: !!po.currencyLocked,
      currencyLockReason: po.currencyLockReason || '',
      items: (po.items && po.items.length ? po.items : [blankItem(t)]).map((it) => ({
        commodityId: it.commodityId || '',
        commodityName: it.commodityName || '',
        commodityCode: it.commodityCode || '',
        hsnCode: it.hsnCode || '',
        gstRate: it.gstRate ?? 18,
        quantity: it.quantity ?? '',
        uom: it.uom || (t === 'service' ? 'Job' : 'Nos'),
        rate: it.rate ?? '',
        amount: it.amount ?? 0,
        itemType: it.itemType || t,
      })),
      miscCharges: (po.miscCharges || []).map((m) => ({
        description: m.description || '', amount: m.amount ?? '',
      })),
      remarks: po.remarks || '',
      status: po.status || 'Ordered',
      advancePaymentConfig: po.advancePaymentConfig?.enabled
        ? po.advancePaymentConfig
        : { enabled: false, splits: [] },
    });
    setShowModal(true);
  };

  const handleVendorSelect = (vendor) => {
    if (!vendor) return;
    setForm((f) => ({
      ...f,
      vendorName: vendor.vendorName || '',
      vendorCode: vendor.vendorCode || '',
      vendorGst: vendor.gstin || vendor.gstNo || '',
      vendorContact: vendor.phone || '',
      vendorContactPerson: vendor.contactPerson || '',
      vendorEmail: vendor.email || '',
      vendorAddress: vendor.address || '',
      vendorCity: vendor.city || '',
      vendorState: vendor.state || '',
      vendorCountry: vendor.country || '',
    }));
  };

  const handleCommoditySelect = (idx, commodity) => {
    setForm((f) => {
      const items = [...f.items];
      items[idx] = {
        ...items[idx],
        commodityId: commodity.id || '',
        commodityName: commodity.commodityName || '',
        commodityCode: commodity.commodityCode || '',
        hsnCode: commodity.hsnCode || '',
        gstRate: commodity.gstRate ?? 18,
        uom: commodity.uom || items[idx].uom || 'Nos',
        itemType: commodity.itemType || 'material',
      };
      return { ...f, items };
    });
  };

  const handleSACSelect = (idx, sac) => {
    setForm((f) => {
      const items = [...f.items];
      items[idx] = {
        ...items[idx],
        commodityId: `sac_${sac.sacCode}`,
        commodityName: sac.sacDescription || '',
        commodityCode: sac.sacCode || '',
        hsnCode: sac.sacCode || '',
        gstRate: sac.gstRate ?? 18,
        itemType: 'service',
      };
      return { ...f, items };
    });
  };

  const updateItem = (idx, key, val) => {
    setForm((f) => {
      const items = [...f.items];
      items[idx] = { ...items[idx], [key]: val };
      if (key === 'quantity' || key === 'rate') {
        const q = parseFloat(key === 'quantity' ? val : items[idx].quantity) || 0;
        const r = parseFloat(key === 'rate' ? val : items[idx].rate) || 0;
        items[idx].amount = +(q * r).toFixed(2);
      }
      return { ...f, items };
    });
  };

  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, blankItem(f.poType)] }));
  const removeItem = (idx) => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const addMisc = () => setForm((f) => ({ ...f, miscCharges: [...f.miscCharges, blankMisc()] }));
  const removeMisc = (idx) => setForm((f) => ({ ...f, miscCharges: f.miscCharges.filter((_, i) => i !== idx) }));
  const updateMisc = (idx, key, val) => {
    setForm((f) => {
      const m = [...f.miscCharges];
      m[idx] = { ...m[idx], [key]: val };
      return { ...f, miscCharges: m };
    });
  };

  // Live totals
  const isINR = (form.currency || 'INR') === 'INR';
  const curSym = currencySymbol(form.currency || 'INR');
  const subtotal = form.items.reduce((s, it) => s + (parseFloat(it.amount) || 0), 0);
  const gstTotal = isINR ? form.items.reduce(
    (s, it) => s + (parseFloat(it.amount) || 0) * ((parseFloat(it.gstRate) || 0) / 100), 0,
  ) : 0;
  const miscTotal = form.miscCharges.reduce((s, m) => s + (parseFloat(m.amount) || 0), 0);
  const grandTotal = subtotal + gstTotal + miscTotal;

  // A misc row is invalid only when it has an amount but no description. A
  // description with no amount is allowed. Save is blocked while any row is invalid,
  // so every saved amount is always visible in the live summary.
  const miscHasErrors = hasMiscErrors(form.miscCharges);

  const handleSave = async (e) => {
    e.preventDefault();
    const retainedLegacy = editingId && retainedProject && !retainedProject.id && form.projectName === retainedProject.name;
    if ((!form.projectId && !retainedLegacy) || !form.vendorName.trim()) {
      alert('Project and Vendor are required.');
      return;
    }
    if (!form.items.length || form.items.every((it) => !it.commodityName.trim())) {
      alert('Add at least one item.');
      return;
    }
    if (miscHasErrors) {
      alert('Each misc charge needs both a description and an amount. Please fix the highlighted rows.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        poType: form.poType || poTab,
        items: form.items
          .filter((it) => it.commodityName && it.commodityName.trim())
          .map((it) => ({
            commodityName: it.commodityName.trim(),
            commodityCode: it.commodityCode || '',
            hsnCode: it.hsnCode || '',
            gstRate: parseFloat(it.gstRate) || 0,
            quantity: parseFloat(it.quantity) || 0,
            uom: it.uom || 'Nos',
            rate: parseFloat(it.rate) || 0,
            amount: parseFloat(it.amount) || 0,
            itemType: it.itemType || form.poType,
          })),
        miscCharges: form.miscCharges
          .filter((m) => m.description && m.description.trim())
          .map((m) => ({ description: m.description.trim(), amount: parseFloat(m.amount) || 0 })),
        advancePaymentConfig: (form.paymentTerms === 'Advance Payment' && form.advancePaymentConfig?.enabled)
          ? {
              enabled: true,
              splits: (form.advancePaymentConfig.splits || []).map((s) => ({
                percent: parseFloat(s.percent) || 0,
                trigger: s.trigger,
                daysAfterGRN: s.trigger === 'AfterGRN' ? (parseInt(s.daysAfterGRN) || 0) : null,
              })),
            }
          : { enabled: false, splits: [] },
      };
      delete payload.currencyLocked;
      delete payload.currencyLockReason;
      if (editingId) {
        await axios.put(`${API}/projects/purchase-orders/${editingId}`, payload);
      } else {
        await axios.post(`${API}/projects/purchase-orders`, payload);
      }
      setShowModal(false);
      setEditingId(null);
      setForm(emptyForm(poTab));
      fetchAll();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (po) => {
    if (!window.confirm(`Delete PO "${po.poNumber || po.id}" (${po.vendorName || po.supplier})? This cannot be undone.`)) return;
    setDeletingId(po.id);
    try {
      await axios.delete(`${API}/projects/purchase-orders/${po.id}`);
      setOrders((prev) => prev.filter((x) => x.id !== po.id));
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to delete');
    } finally {
      setDeletingId(null);
    }
  };

  const handleDownloadPdf = async (po) => {
    try {
      const resp = await axios.get(`${API}/projects/purchase-orders/${po.id}/pdf`, { responseType: 'blob' });
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${po.poNumber || po.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to download PDF');
    }
  };

  const openEmail = (po) => {
    const poNumber = po.poNumber || po.id;
    const total = (po.totalAmount || po.amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
    const currency = po.currency || 'INR';
    setEmailPo(po);
    setEmailResult(null);
    axios.get(`${API}/projects/email-status`)
      .then((r) => setEmailConfig({ senderEmail: r.data?.senderEmail || '', isSandbox: !!r.data?.isSandbox }))
      .catch(() => setEmailConfig({ senderEmail: '', isSandbox: false }));
    setEmailForm({
      to: po.vendorEmail || '',
      cc: '',
      subject: `Purchase Order ${poNumber} — ${po.projectName || ''}`.trim().replace(/—\s*$/, ''),
      body: [
        `Dear ${po.vendorName || po.supplier || 'Vendor'},`,
        '',
        `Please find attached our Purchase Order ${poNumber} for the project "${po.projectName || ''}".`,
        '',
        `Grand Total: ${currency} ${total}`,
        '',
        'Kindly acknowledge receipt and confirm delivery as per the schedule mentioned.',
        '',
        'Regards,',
        'Swift Strips India',
      ].join('\n'),
    });
  };

  const closeEmail = () => {
    setEmailPo(null);
    setEmailForm({ to: '', cc: '', subject: '', body: '' });
    setEmailResult(null);
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    if (!emailForm.to.trim()) {
      setEmailResult({ success: false, message: 'Recipient email is required.' });
      return;
    }
    setEmailing(true);
    setEmailResult(null);
    try {
      const htmlBody = `<div>${(emailForm.body || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br/>')}</div>`;
      const resp = await axios.post(
        `${API}/projects/purchase-orders/${emailPo.id}/email`,
        { to: emailForm.to, cc: emailForm.cc, subject: emailForm.subject, body: htmlBody },
      );
      setEmailResult({
        success: true,
        message: `Email sent successfully${resp.data?.messageId ? ` (id: ${resp.data.messageId})` : ''}.`,
      });
    } catch (err) {
      setEmailResult({
        success: false,
        message: err?.response?.data?.detail || err.message || 'Failed to send email.',
      });
    } finally {
      setEmailing(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Delivered': return 'bg-green-100 text-green-700';
      case 'In Transit': return 'bg-blue-100 text-blue-700';
      case 'Ordered': return 'bg-yellow-100 text-yellow-700';
      case 'Cancelled': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const tabbedOrders = orders.filter((o) => {
    const t = o.poType === 'service' ? 'service' : 'material';
    return t === poTab;
  });

  const projectOptions = projectFilterOptions(directory.projects, orders);
  const selectedProjectFilter = projectOptions.find((p) => p.value === filterProject);
  const isUnemailed = (o) => !o.lastEmailedAt || o.lastEmailedSuccess === false;
  const filteredOrders = tabbedOrders
    .filter((o) => filterProject === 'all' || matchesProjectFilter(o, selectedProjectFilter))
    .filter((o) => !showOnlyUnemailed || isUnemailed(o))
    .filter((o) => !search.trim() ||
      (o.poNumber || '').toLowerCase().includes(search.toLowerCase()) ||
      (o.vendorName || o.supplier || '').toLowerCase().includes(search.toLowerCase()) ||
      (o.projectName || '').toLowerCase().includes(search.toLowerCase()),
    );

  const stats = {
    total: tabbedOrders.length,
    delivered: tabbedOrders.filter((o) => o.status === 'Delivered').length,
    inTransit: tabbedOrders.filter((o) => o.status === 'In Transit').length,
    totalValue: tabbedOrders.reduce((sum, o) => sum + (o.totalAmount || o.amount || 0), 0),
    unemailed: tabbedOrders.filter(isUnemailed).length,
  };

  if (loading) return <div className="text-center py-12">Loading purchase orders...</div>;

  const isService = poTab === 'service';
  const accent = isService ? 'indigo' : 'amber';

  return (
    <div className="space-y-6" data-testid="project-purchase-orders-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Project Purchase Orders</h1>
          <p className="text-gray-500 mt-1">
            Material &amp; Service POs · HSN/SAC auto-fetch from GST master · mirrors Stores PO UX.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {stats.unemailed > 0 && (
            <button
              onClick={() => setShowOnlyUnemailed((v) => !v)}
              className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium border transition ${
                showOnlyUnemailed
                  ? 'bg-amber-500 text-white border-amber-500 shadow-md hover:bg-amber-600'
                  : 'bg-amber-50 text-amber-800 border-amber-300 hover:bg-amber-100'
              }`}
              data-testid="unemailed-filter-badge"
            >
              <Mail size={14} />
              <span>{stats.unemailed} not emailed{showOnlyUnemailed && ' ·'}</span>
              {showOnlyUnemailed && <X size={14} />}
            </button>
          )}
          {canEdit && (
            <button onClick={openCreate}
              className={`bg-gradient-to-r ${isService ? 'from-indigo-600 to-indigo-700' : 'from-[#1a237e] to-[#283593]'} text-white px-4 py-2.5 rounded-xl text-sm font-medium flex items-center space-x-2 shadow-md hover:opacity-90`}
              data-testid="create-po-btn">
              <Plus size={18} />
              <span>Create {isService ? 'Service ' : ''}PO</span>
            </button>
          )}
        </div>
      </div>

      {/* Material / Service tabs */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="flex">
          <button
            onClick={() => { setPoTab('material'); setShowModal(false); }}
            className={`flex-1 flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold transition border-b-2 ${
              poTab === 'material'
                ? 'border-amber-500 text-amber-700 bg-amber-50'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
            data-testid="material-po-tab"
          >
            <Package size={18} />
            Material PO
          </button>
          <button
            onClick={() => { setPoTab('service'); setShowModal(false); }}
            className={`flex-1 flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold transition border-b-2 ${
              poTab === 'service'
                ? 'border-indigo-500 text-indigo-700 bg-indigo-50'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
            data-testid="service-po-tab"
          >
            <Briefcase size={18} />
            Service PO
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
          <div className="flex items-center space-x-2"><Package className="text-[#1a237e]" size={18} /><p className="text-sm text-gray-500">Total {isService ? 'Service' : 'Material'} POs</p></div>
          <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
        </div>
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-5 border border-green-200">
          <div className="flex items-center space-x-2"><CheckCircle className="text-green-500" size={18} /><p className="text-sm text-green-600 font-medium">Delivered</p></div>
          <p className="text-2xl font-bold text-green-700">{stats.delivered}</p>
        </div>
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-5 border border-blue-200">
          <div className="flex items-center space-x-2"><Truck className="text-blue-500" size={18} /><p className="text-sm text-blue-600 font-medium">In Transit</p></div>
          <p className="text-2xl font-bold text-blue-700">{stats.inTransit}</p>
        </div>
        <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
          <p className="text-sm text-gray-500">Total Value</p>
          <p className="text-2xl font-bold text-gray-900">₹{(stats.totalValue / 100000).toFixed(1)} L</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center flex-wrap gap-2 min-w-0">
        <Search size={16} className="text-gray-400" />
        <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
          data-testid="project-po-search"
          placeholder="Search PO #, vendor, project…"
          className={`px-3 py-2 border border-gray-200 rounded-xl bg-white text-sm focus:ring-2 focus:ring-${accent}-500/20 outline-none w-72 max-w-full`} />
        <select value={filterProject} onChange={(e) => setFilterProject(e.target.value)}
          data-testid="project-po-project-filter" aria-label="Filter by project"
          className="px-4 py-2 border border-gray-200 rounded-xl bg-white text-sm outline-none max-w-full min-w-0">
          <option value="all">All Projects</option>
          {projectOptions.map((p) => (<option key={p.value} value={p.value} data-testid={`project-filter-option-${p.value}`}>{p.id ? `${p.id} — ` : ''}{p.name}{p.status === 'Ongoing' ? '' : ` (${p.status})`}</option>))}
        </select>
        {directory.error && <p role="alert" data-testid="project-po-directory-error" className="text-sm text-red-700">{directory.error} <button type="button" onClick={directory.refresh} className="underline" data-testid="project-po-directory-retry">Retry</button></p>}
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-gray-50 to-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">PO Number</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Project</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Vendor</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Items</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Amount</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">PO Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Delivery</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Email</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredOrders.map((po) => (
                <tr key={po.id} className="hover:bg-gray-50" data-testid={`po-row-${po.id}`}>
                  <td className="px-4 py-3 font-mono text-sm text-[#1a237e] font-medium">{po.poNumber || po.id}</td>
                  <td className="px-4 py-3">
                    <span className="text-xs bg-indigo-50 text-[#1a237e] px-2 py-0.5 rounded font-mono">{po.projectId}</span>
                    <p className="text-sm text-gray-600 mt-0.5">{po.projectName}</p>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">{po.vendorName || po.supplier}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{(po.items || []).length} item{(po.items || []).length === 1 ? '' : 's'}</td>
                  <td className="px-4 py-3 text-right text-sm font-semibold text-gray-900 tabular-nums">₹{((po.totalAmount || po.amount) || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{po.poDate || po.orderDate}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{po.deliveryDate || '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(po.status)}`}>{po.status}</span>
                  </td>
                  <td className="px-4 py-3" data-testid={`email-status-${po.id}`}>
                    {po.lastEmailedAt ? (() => {
                      const sent = new Date(po.lastEmailedAt);
                      const now = new Date();
                      const diffMs = now - sent;
                      const minutes = Math.floor(diffMs / 60000);
                      const hours = Math.floor(minutes / 60);
                      const days = Math.floor(hours / 24);
                      const relative = minutes < 1 ? 'just now'
                        : minutes < 60 ? `${minutes}m ago`
                        : hours < 24 ? `${hours}h ago`
                        : days < 30 ? `${days}d ago`
                        : sent.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
                      const recipients = (po.lastEmailedTo || []).join(', ');
                      const tooltip = po.lastEmailedSuccess
                        ? `Sent to ${recipients || 'vendor'} on ${sent.toLocaleString('en-IN')}${po.lastEmailedBy ? ` by ${po.lastEmailedBy}` : ''}`
                        : `Last attempt FAILED on ${sent.toLocaleString('en-IN')}${po.lastEmailedError ? ` — ${po.lastEmailedError}` : ''}`;
                      const cls = po.lastEmailedSuccess
                        ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                        : 'bg-rose-50 text-rose-800 border-rose-200';
                      return (
                        <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border ${cls}`} title={tooltip}>
                          {po.lastEmailedSuccess ? <CheckCircle size={11} /> : <X size={11} />}
                          <span>{po.lastEmailedSuccess ? `Sent ${relative}` : `Failed ${relative}`}</span>
                        </div>
                      );
                    })() : (
                      <span className="text-[11px] text-gray-400 italic">not sent</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => handleDownloadPdf(po)}
                        className="p-1.5 text-indigo-700 hover:bg-indigo-50 rounded"
                        title="Download PDF"
                        data-testid={`download-pdf-${po.id}`}>
                        <Download size={14} />
                      </button>
                      <button onClick={() => openEmail(po)}
                        className="p-1.5 text-teal-700 hover:bg-teal-50 rounded"
                        title="Email PO to Vendor"
                        data-testid={`email-po-${po.id}`}>
                        <Mail size={14} />
                      </button>
                      {canEdit && (
                        <button onClick={() => openEdit(po)}
                          className="p-1.5 text-blue-700 hover:bg-blue-50 rounded"
                          title="Edit"
                          data-testid={`edit-po-${po.id}`}>
                          <Pencil size={14} />
                        </button>
                      )}
                      {canDelete && (
                        <button onClick={() => handleDelete(po)} disabled={deletingId === po.id}
                          className="p-1.5 text-rose-600 hover:bg-rose-50 rounded disabled:opacity-50"
                          title="Delete"
                          data-testid={`delete-po-${po.id}`}>
                          {deletingId === po.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filteredOrders.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No {isService ? 'service' : 'material'} purchase orders yet.
          </div>
        )}
      </div>

      {/* Create / Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="po-modal">
          <form onSubmit={handleSave} className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[92vh] overflow-y-auto">
            <div className={`flex items-center justify-between p-5 border-b border-gray-200 bg-gradient-to-r ${isService ? 'from-indigo-600 to-indigo-700' : 'from-[#1a237e] to-[#283593]'} rounded-t-2xl sticky top-0 z-10`}>
              <h2 className="text-lg font-bold text-white">
                {editingId ? 'Edit' : 'Create'} {isService ? 'Service' : 'Material'} Purchase Order
              </h2>
              <button type="button" onClick={() => setShowModal(false)} data-testid="project-po-modal-close" aria-label="Close purchase order" className="p-2 text-white/80 hover:bg-white/10 rounded-lg"><X size={20} /></button>
            </div>
            <div className="p-5 space-y-5">
              {/* Project */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="md:col-span-2">
                  <label className="text-xs font-medium text-gray-600 uppercase">Project *</label>
                  <ProjectSelect value={form.projectId} retainedProject={retainedProject}
                    onChange={(p) => setForm((f) => ({ ...f, projectId: p.id, projectName: p.name }))} />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 uppercase">PO Type</label>
                  <input type="text" readOnly value={isService ? 'Service' : 'Material'}
                    className="mt-1 w-full px-3 py-2 border border-gray-200 bg-gray-50 rounded-lg text-sm" />
                </div>
              </div>

              {/* Vendor block — uses Stores Vendor master */}
              <div className={`border ${isService ? 'border-indigo-200 bg-indigo-50/40' : 'border-amber-200 bg-amber-50/40'} rounded-xl p-4 space-y-3`}>
                <h3 className="font-semibold text-gray-700 text-sm flex items-center gap-2">
                  <Building2 size={16} className={isService ? 'text-indigo-600' : 'text-amber-600'} />
                  Vendor Details (from Stores Vendor master)
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="md:col-span-2">
                    <label className="text-xs text-gray-600">Vendor Name *</label>
                    <VendorDropdown
                      displayValue={form.vendorName ? `${form.vendorName}${form.vendorCode ? ` (${form.vendorCode})` : ''}` : ''}
                      onSelect={handleVendorSelect}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-600">Vendor Code</label>
                    <input type="text" readOnly value={form.vendorCode}
                      className="mt-1 w-full px-3 py-2 border border-gray-200 bg-gray-50 rounded-lg text-sm" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-600">GSTIN</label>
                    <input type="text" readOnly value={form.vendorGst}
                      className="mt-1 w-full px-3 py-2 border border-gray-200 bg-gray-50 rounded-lg text-sm" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-600">Contact Person</label>
                    <input type="text" readOnly value={form.vendorContactPerson}
                      className="mt-1 w-full px-3 py-2 border border-gray-200 bg-gray-50 rounded-lg text-sm" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-600">Contact No.</label>
                    <input type="text" readOnly value={form.vendorContact}
                      className="mt-1 w-full px-3 py-2 border border-gray-200 bg-gray-50 rounded-lg text-sm" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-xs text-gray-600">Address</label>
                    <input type="text" readOnly
                      value={[form.vendorAddress, form.vendorCity, form.vendorState, form.vendorCountry].filter(Boolean).join(', ')}
                      className="mt-1 w-full px-3 py-2 border border-gray-200 bg-gray-50 rounded-lg text-sm" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-600">Email <span className="text-gray-400">(used for &quot;Email PO&quot;)</span></label>
                    <input type="email" value={form.vendorEmail}
                      onChange={(e) => setForm((p) => ({ ...p, vendorEmail: e.target.value }))}
                      placeholder="vendor@example.com"
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      data-testid="po-vendor-email-input" />
                  </div>
                </div>
              </div>

              {/* Dates / Terms */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <label className="text-xs text-gray-600">PO Date</label>
                  <input type="date" value={form.poDate}
                    onChange={(e) => setForm((p) => ({ ...p, poDate: e.target.value }))}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="text-xs text-gray-600">Delivery Start Date</label>
                  <input type="date" value={form.deliveryDate}
                    onChange={(e) => setForm((p) => ({ ...p, deliveryDate: e.target.value }))}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="text-xs text-gray-600">Delivery End Date</label>
                  <input type="date" value={form.deliveryEndDate}
                    onChange={(e) => setForm((p) => ({ ...p, deliveryEndDate: e.target.value }))}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="text-xs text-gray-600">Payment Terms</label>
                  <select
                    value={form.paymentTerms}
                    onChange={(e) => {
                      const v = e.target.value;
                      setForm((p) => ({
                        ...p,
                        paymentTerms: v,
                        advancePaymentConfig: (v === 'Advance Payment')
                          ? (p.advancePaymentConfig?.enabled ? p.advancePaymentConfig : {
                              enabled: true,
                              splits: [{ percent: 100, trigger: 'Advance', daysAfterGRN: null }],
                            })
                          : { enabled: false, splits: [] },
                      }));
                    }}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    data-testid="payment-terms-select"
                  >
                    <option value="">Select…</option>
                    {paymentTermsList.map((pt) => (
                      <option key={pt.id} value={pt.paymentName}>{pt.paymentName}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-600">Currency</label>
                  <select
                    value={form.currency}
                    disabled={!!form.currencyLocked}
                    onChange={(e) => {
                      const next = e.target.value;
                      if (next === form.currency) return;
                      const ok = window.confirm(
                        'Changing the currency does NOT convert existing rates or amounts. ' +
                        'All item rates and amounts must be re-entered in the new currency. Continue?');
                      if (ok) setForm((p) => ({ ...p, currency: next }));
                    }}
                    className={`mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm ${form.currencyLocked ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    data-testid="po-currency-select"
                  >
                    {CURRENCY_OPTIONS.map((c) => (
                      <option key={c.code} value={c.code}>{c.code} - {c.name}</option>
                    ))}
                  </select>
                  {form.currencyLocked && (
                    <p data-testid="po-currency-lock-reason" className="mt-1 text-[11px] text-amber-700">
                      {form.currencyLockReason || 'Currency is locked for this PO.'}
                    </p>
                  )}
                </div>
              </div>

              {/* Advance Payment Split Config */}
              {form.paymentTerms === 'Advance Payment' && (
                <AdvancePaymentSplitConfig
                  value={form.advancePaymentConfig}
                  onChange={(next) => setForm((p) => ({ ...p, advancePaymentConfig: next }))}
                />
              )}

              {/* Items */}
              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <div className={`flex items-center justify-between p-3 border-b border-gray-200 ${isService ? 'bg-indigo-50' : 'bg-amber-50'}`}>
                  <h3 className="font-semibold text-gray-700 text-sm flex items-center gap-2">
                    {isService ? <Briefcase size={16} className="text-indigo-600" /> : <Package size={16} className="text-amber-600" />}
                    {isService ? 'Service Items' : 'Line Items'}
                  </h3>
                  <button type="button" onClick={addItem}
                    className={`text-xs px-2 py-1 rounded text-white flex items-center gap-1 ${isService ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-amber-600 hover:bg-amber-700'}`}
                    data-testid="add-item-btn">
                    <Plus size={12} /> Add {isService ? 'Service' : 'Item'}
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-2 py-2 text-left text-xs font-semibold text-gray-600 w-80" colSpan={2}>
                          {isService ? 'Service (SAC + Name) *' : 'Commodity (HSN + Name) *'}
                        </th>
                        {isINR && <th className="px-2 py-2 text-right text-xs font-semibold text-gray-600">GST %</th>}
                        <th className="px-2 py-2 text-right text-xs font-semibold text-gray-600">Qty</th>
                        <th className="px-2 py-2 text-left text-xs font-semibold text-gray-600">UOM</th>
                        <th className="px-2 py-2 text-right text-xs font-semibold text-gray-600">Rate</th>
                        <th className="px-2 py-2 text-right text-xs font-semibold text-gray-600">Amount</th>
                        <th className="px-2 py-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {form.items.map((item, idx) => (
                        <tr key={idx} className="border-t border-gray-100 align-top">
                          <td className="px-2 py-2 w-80" colSpan={2}>
                            <HsnCommodityPicker
                              kind={isService ? 'service' : 'commodity'}
                              hsnCode={item.hsnCode}
                              commodityName={item.commodityName}
                              gstRate={item.gstRate}
                              commodityId={item.commodityId}
                              onChange={(patch) => {
                                setForm(prev => {
                                  const newItems = [...prev.items];
                                  newItems[idx] = {
                                    ...newItems[idx],
                                    ...patch,
                                    ...(isService && patch.hsnCode !== undefined
                                      ? { commodityCode: patch.hsnCode }
                                      : {}),
                                    itemType: isService ? 'service' : 'material',
                                  };
                                  if (patch.gstRate !== undefined || patch.commodityName !== undefined) {
                                    const qty = parseFloat(newItems[idx].quantity) || 0;
                                    const rate = parseFloat(newItems[idx].rate) || 0;
                                    newItems[idx].amount = qty * rate;
                                  }
                                  return { ...prev, items: newItems };
                                });
                              }}
                              testIdPrefix={`project-po-item-${idx}`}
                            />
                          </td>
                          {isINR && (
                            <td className="px-2 py-2">
                              <input type="number" step="0.01" value={item.gstRate}
                                onChange={(e) => updateItem(idx, 'gstRate', e.target.value)}
                                className="w-16 px-2 py-1 border border-gray-300 rounded text-sm text-right" />
                            </td>
                          )}
                          <td className="px-2 py-2">
                            <input type="number" step="0.01" value={item.quantity}
                              onChange={(e) => updateItem(idx, 'quantity', e.target.value)}
                              className="w-20 px-2 py-1 border border-gray-300 rounded text-sm text-right" />
                          </td>
                          <td className="px-2 py-2">
                            <select value={item.uom} onChange={(e) => updateItem(idx, 'uom', e.target.value)}
                              className="w-24 px-2 py-1 border border-gray-300 rounded text-sm"
                              data-testid={`po-item-uom-${idx}`}>
                              <option value="">Select</option>
                              {uomList.length > 0 ? (
                                uomList.map((u) => (
                                  <option key={u.id || u.uomCode} value={u.uomCode}>{u.uomCode}</option>
                                ))
                              ) : (
                                <>
                                  <option value="Nos">Nos</option>
                                  <option value="KGS">KGS</option>
                                  <option value="MT">MT</option>
                                  <option value="LTR">LTR</option>
                                  <option value="PCS">PCS</option>
                                  <option value="MTR">MTR</option>
                                </>
                              )}
                              {isService && (
                                <>
                                  <option value="Job">Job</option>
                                  <option value="Lump Sum">Lump Sum</option>
                                  <option value="Per Trip">Per Trip</option>
                                  <option value="Per Day">Per Day</option>
                                  <option value="Per Month">Per Month</option>
                                  <option value="Per Hour">Per Hour</option>
                                  <option value="Sq.ft">Sq.ft</option>
                                  <option value="Sq.mt">Sq.mt</option>
                                  <option value="Rft">Rft</option>
                                </>
                              )}
                            </select>
                          </td>
                          <td className="px-2 py-2">
                            <input type="number" step="0.01" value={item.rate}
                              onChange={(e) => updateItem(idx, 'rate', e.target.value)}
                              className="w-24 px-2 py-1 border border-gray-300 rounded text-sm text-right" />
                          </td>
                          <td className="px-2 py-2 text-right tabular-nums font-medium text-gray-700">
                            {curSym}{(item.amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                          </td>
                          <td className="px-2 py-2">
                            <button type="button" onClick={() => removeItem(idx)} disabled={form.items.length === 1}
                              className="p-1 text-rose-600 hover:bg-rose-50 rounded disabled:opacity-30">
                              <Trash2 size={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Misc charges */}
              <div className="border border-gray-200 rounded-xl">
                <div className="flex items-center justify-between p-3 border-b border-gray-200 bg-amber-50">
                  <h3 className="font-semibold text-gray-700 text-sm">
                    Misc Charges <span className="text-xs text-gray-500 font-normal">(non-GST: transport, handling, etc.)</span>
                  </h3>
                  <button type="button" onClick={addMisc}
                    className="text-xs px-2 py-1 rounded bg-amber-600 text-white flex items-center gap-1">
                    <Plus size={12} /> Add Charge
                  </button>
                </div>
                {miscHasErrors && (
                  <div data-testid="misc-validation-error"
                    className="mx-3 mt-3 px-3 py-2 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-xs">
                    Each misc charge needs both a description and an amount. Fix the highlighted rows below before saving.
                  </div>
                )}
                {form.miscCharges.length === 0 ? (
                  <div className="text-center py-3 text-xs text-gray-500">No misc charges.</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-2 py-2 text-left text-xs font-semibold text-gray-600">Description</th>
                        <th className="px-2 py-2 text-right text-xs font-semibold text-gray-600">Amount</th>
                        <th className="px-2 py-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {form.miscCharges.map((m, idx) => {
                        const rowErr = miscRowError(m);
                        return (
                        <tr key={idx} className="border-t border-gray-100 align-top">
                          <td className="px-2 py-1">
                            <input type="text" value={m.description}
                              data-testid={`misc-desc-${idx}`}
                              onChange={(e) => updateMisc(idx, 'description', e.target.value)}
                              className={`w-full px-2 py-1 border rounded text-sm ${rowErr ? 'border-rose-400 bg-rose-50' : 'border-gray-300'}`} />
                            {rowErr && (
                              <div data-testid={`misc-row-error-${idx}`} className="text-[11px] text-rose-600 mt-1">{rowErr}</div>
                            )}
                          </td>
                          <td className="px-2 py-1"><input type="number" step="0.01" value={m.amount}
                            data-testid={`misc-amount-${idx}`}
                            onChange={(e) => updateMisc(idx, 'amount', e.target.value)}
                            className={`w-32 px-2 py-1 border rounded text-sm text-right ${rowErr ? 'border-rose-400 bg-rose-50' : 'border-gray-300'}`} /></td>
                          <td className="px-2 py-1">
                            <button type="button" onClick={() => removeMisc(idx)} className="p-1 text-rose-600 hover:bg-rose-50 rounded">
                              <Trash2 size={14} />
                            </button>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Terms & Conditions (multi-select, sourced from Stores master) */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-medium text-gray-600">Terms &amp; Conditions</label>
                  {(() => {
                    const total = form.termsConditions.length;
                    const valid = form.termsConditions.filter(id => termsConditionsList.find(t => t.id === id)).length;
                    const orphan = total - valid;
                    return (
                      <span className="text-xs text-gray-400" data-testid="tc-selected-count">
                        {valid} selected
                        {orphan > 0 && (
                          <span className="ml-1 text-amber-700 italic" title={`${orphan} term(s) on this PO no longer exist in master data`}>
                            (+{orphan} deleted)
                          </span>
                        )}
                      </span>
                    );
                  })()}
                </div>
                {form.termsConditions.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2 p-2 bg-gray-50 rounded-lg">
                    {form.termsConditions.map((termId, index) => {
                      const term = termsConditionsList.find((t) => t.id === termId);
                      return term ? (
                        <div key={index}
                          className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
                            isService ? 'bg-indigo-100 text-indigo-800' : 'bg-amber-100 text-amber-800'
                          }`}
                          data-testid={`tc-chip-${index}`}
                        >
                          <span className="max-w-[260px] truncate" title={term.termDescription}>
                            {(term.termDescription || term.termTitle || '').substring(0, 60)}
                            {(term.termDescription || term.termTitle || '').length > 60 ? '…' : ''}
                          </span>
                          <button
                            type="button"
                            onClick={() => setForm((p) => ({
                              ...p,
                              termsConditions: p.termsConditions.filter((id) => id !== termId),
                            }))}
                            className={`p-0.5 rounded-full ${isService ? 'hover:bg-indigo-200' : 'hover:bg-amber-200'}`}
                          >
                            <X size={12} />
                          </button>
                        </div>
                      ) : null;
                    })}
                  </div>
                )}
                <select
                  value=""
                  onChange={(e) => {
                    if (e.target.value && !form.termsConditions.includes(e.target.value)) {
                      setForm((p) => ({
                        ...p,
                        termsConditions: [...p.termsConditions, e.target.value],
                      }));
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  data-testid="tc-select"
                >
                  <option value="">+ Add terms &amp; conditions…</option>
                  {termsConditionsList
                    .filter((t) => !form.termsConditions.includes(t.id))
                    .map((tc) => (
                      <option key={tc.id} value={tc.id}>
                        {tc.termDescription || tc.termTitle}
                      </option>
                    ))}
                </select>
              </div>

              {/* Totals + Remarks */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-600">Remarks</label>
                  <textarea rows={3} value={form.remarks}
                    onChange={(e) => setForm((p) => ({ ...p, remarks: e.target.value }))}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div className={`${isService ? 'bg-indigo-50 border-indigo-200' : 'bg-amber-50 border-amber-200'} border rounded-xl p-4 space-y-2 text-sm`}>
                  <div className="flex justify-between"><span>Subtotal:</span><span className="font-mono">{curSym}{subtotal.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span></div>
                  {isINR && (
                    <div className="flex justify-between"><span>GST Amount:</span><span className="font-mono">{curSym}{gstTotal.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span></div>
                  )}
                  <div className="flex justify-between"><span>Misc Charges:</span><span className="font-mono">{curSym}{miscTotal.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span></div>
                  <div className={`border-t ${isService ? 'border-indigo-300' : 'border-amber-300'} pt-2 flex justify-between font-bold ${isService ? 'text-indigo-900' : 'text-amber-900'}`}>
                    <span>GRAND TOTAL:</span><span className="font-mono">{curSym}{grandTotal.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="p-4 border-t border-gray-200 flex justify-end gap-2 bg-gray-50 rounded-b-2xl sticky bottom-0">
              <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button type="submit" disabled={saving}
                className={`flex items-center gap-2 px-4 py-2 text-white rounded-lg disabled:opacity-60 ${isService ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-[#1a237e] hover:bg-[#283593]'}`}
                data-testid="po-save-btn">
                <Save size={16} /> {saving ? 'Saving…' : (editingId ? 'Save Changes' : `Create ${isService ? 'Service ' : ''}PO`)}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Email PO Modal */}
      {emailPo && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="email-po-modal">
          <form onSubmit={handleSendEmail} className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-200 bg-gradient-to-r from-teal-600 to-teal-700 rounded-t-2xl">
              <div>
                <h2 className="text-lg font-bold text-white flex items-center gap-2"><Mail size={18} /> Email Purchase Order</h2>
                <p className="text-xs text-teal-100 mt-0.5">PO {emailPo.poNumber || emailPo.id} → {emailPo.vendorName || emailPo.supplier}</p>
              </div>
              <button type="button" onClick={closeEmail} className="p-2 text-white/80 hover:bg-white/10 rounded-lg"><X size={20} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className={`border rounded-lg p-3 text-xs ${emailConfig.isSandbox ? 'bg-amber-50 border-amber-200 text-amber-900' : 'bg-emerald-50 border-emerald-200 text-emerald-900'}`}>
                <strong>The PO PDF will be auto-attached.</strong>{' '}
                Sender: <code className="bg-white px-1 rounded">{emailConfig.senderEmail || 'loading…'}</code>.
                {emailConfig.isSandbox
                  ? ' In Resend sandbox mode, delivery is restricted to verified addresses until a domain is verified.'
                  : ' Verified sender — emails will deliver to any recipient.'}
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 uppercase">To *</label>
                <input type="text" required value={emailForm.to}
                  onChange={(e) => setEmailForm((p) => ({ ...p, to: e.target.value }))}
                  placeholder="vendor@example.com (comma-separate for multiple)"
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  data-testid="email-to-input" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 uppercase">CC</label>
                <input type="text" value={emailForm.cc}
                  onChange={(e) => setEmailForm((p) => ({ ...p, cc: e.target.value }))}
                  placeholder="purchase@swiftstrips.com (optional)"
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  data-testid="email-cc-input" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 uppercase">Subject</label>
                <input type="text" value={emailForm.subject}
                  onChange={(e) => setEmailForm((p) => ({ ...p, subject: e.target.value }))}
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  data-testid="email-subject-input" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 uppercase">Message</label>
                <textarea rows={8} value={emailForm.body}
                  onChange={(e) => setEmailForm((p) => ({ ...p, body: e.target.value }))}
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono"
                  data-testid="email-body-input" />
              </div>
              {emailResult && (
                <div className={`rounded-lg border p-3 text-sm flex items-start gap-2 ${emailResult.success ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-rose-50 border-rose-200 text-rose-800'}`}
                  data-testid="email-result">
                  {emailResult.success ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
                  <span>{emailResult.message}</span>
                </div>
              )}
            </div>
            <div className="p-4 border-t border-gray-200 flex justify-end gap-2 bg-gray-50 rounded-b-2xl">
              <button type="button" onClick={closeEmail} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Close</button>
              <button type="submit" disabled={emailing || (emailResult && emailResult.success)}
                className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-60"
                data-testid="email-send-btn">
                {emailing ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                {emailing ? 'Sending…' : (emailResult && emailResult.success ? 'Sent' : 'Send Email')}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

export default ProjectPurchaseOrders;
